<ng-container *ngIf="claim$ | async as claim">
  <vs-app-page
    *vsLet="isMobile$ | async as isMobile"
    [config]="{
      icon: 'file-06',
      title: 'Claim #'+ claim.claimNumber,
      description: 'Opened: ' + (claim.created | date) + ' | Last updated: ' + (claim.modified | amTimeAgo: true) + ' ago'
    }"
  >
    <vs-badge slot="title-end" [theme]="claimTheme(claim)" [config]="{ displayStatusIcon: true }">
      {{ claim.status.replace('CANCELLED', 'CANCELED') | titlecase }}
    </vs-badge>

    <vs-breadcrumbs *vsLet="permissionKey$ | async" slot="breadcrumbs">
      <vs-breadcrumbs-item [config]="{ label: 'Claims', routerLink: '..' }"></vs-breadcrumbs-item>
      <vs-breadcrumbs-item [config]="{ label: 'Claim #' + claim.claimNumber }"></vs-breadcrumbs-item>
    </vs-breadcrumbs>

    <div class="flex gap-2 flex-wrap" slot="header-end">
      <!-- Action buttons for open claims -->
      <div *ngIf="claim.status === open" class="flex gap-2 flex-wrap">
        <!-- An authorized claim must be reversed before it can be expired or cancelled -->
        <vs-input-group *ngIf="!claim.frozen" theme="danger">
          <button
            *ngIf="canMarkClaimAsExpired$ | async"
            vs-button
            tooltipStyle="light"
            appearance="outline"
            [vsTooltip]="'Expire' + ((claim.warranty?.expiryDate || 0) < now ? 'd' : 's') + ': ' + (claim.warranty?.expiryDate | date)"
            (click)="openExpireDialog()"
          >
            <vs-icon name="hourglass-03"></vs-icon> Mark as Expired
          </button>

          <button *ngIf="canCancelClaim$ | async" vs-button appearance="outline" (click)="openCancelDialog()">
            <vs-icon name="file-x-02"></vs-icon> Cancel Claim
          </button>
        </vs-input-group>

        <button
          *ngIf="canCloseClaim$ | async"
          vs-button
          appearance="outline"
          [class.flex-grow]="isMobile"
          (click)="closeClaimDialog.open(claim)"
        >
          {{ closeConfig.open }}
        </button>
      </div>

      <!-- The workspace is part of the new claims flow -->
      <a
        *ngIf="usesNewFlow(claim)"
        vs-button
        appearance="outline"
        [routerLink]="'/claims/' + claim.id + '/workspace'"
        data-testid="open-workspace"
      >
        <vs-icon name="layout-left"></vs-icon> {{ workspaceConfig.openWorkspace }}
      </a>

      <!-- View Declaration button -->
      <ws-view-declaration-button
        *ngIf="(canShowDeclaration$ | async) && claim.warranty?.id"
        appearance="outline"
        theme="default"
        [buttonClass]="'flex items-center gap-2'"
        [warrantyId]="claim.warranty!.id"
        [contractNumber]="claim.warranty!.contractNumber"
        [showSuccessToast]="false"
        [blockedText]="'Please allow pop-ups to view the declaration.'"
      ></ws-view-declaration-button>

      <!-- Intercom deep link button - only visible to Global Administrators -->
      <button
        *ngIf="canViewInIntercom$ | async"
        vs-button
        appearance="outline"
        theme="primary"
        (click)="viewInIntercom(claim)"
        [disabled]="!claim?.warranty?.customer?.email"
        [vsTooltip]="!claim?.warranty?.customer?.email ? 'Customer email required' : 'View customer in Intercom'"
      >
        <vs-icon name="link-external-01"></vs-icon> View in Intercom
      </button>
    </div>

    <div
      *ngIf="showsPreviousFlowNote$ | async"
      class="flex gap-3 items-start rounded-xl border border-info-200 bg-info-50 px-4 py-3 mb-4"
      data-testid="previous-flow-note"
    >
      <vs-icon name="info-circle" class="text-info-600 shrink-0 mt-0.5"></vs-icon>
      <div>
        <p class="text-sm font-semibold text-info-800">{{ workspaceConfig.previousFlowNote.title }}</p>
        <p class="text-sm text-info-700">{{ workspaceConfig.previousFlowNote.text }}</p>
      </div>
    </div>

    <div class="flex justify-between gap-2 sm:overflow-x-auto scrollbar-thin sm:flex-nowrap flex-wrap mb-4">
      <vs-info-card
        class="basis-[99.8%] sm:min-w-[256px] min-w-[99.8%]"
        [class]="claim.warranty?.distributor ? 'sm:basis-[24.5%] sm:max-w-[24.5%]' : 'sm:basis-[32.8%] sm:min-w-[32.8%]' "
        [config]="{
          imageUrl: claim.warranty?.customer?.user?.photoURL || undefined,
          icon: claim.warranty?.customer?.user?.photoURL ? undefined : 'user-01',

          title: claim.warranty?.customer?.name,
          subtitle: claim.warranty?.customer?.email,
          subtitleIcon: 'mail-01',

          routerLink: (canViewCustomerDetails$ | async) ? '../../customers/' + claim.warranty?.customer?.id : undefined
        }"
      ></vs-info-card>

      <vs-info-card
        *ngIf="claim.warranty?.asset as asset"
        class="basis-[99.8%] sm:min-w-[256px] min-w-[99.8%]"
        [class]="claim.warranty?.distributor ? 'sm:basis-[24.5%] sm:max-w-[24.5%]' : 'sm:basis-[32.8%] sm:min-w-[32.8%]' "
        [config]="{
          icon: $any(asset.propertySet?.icon || 'home-03'),
          title: asset.name,
          subtitleStyles: '-mt-0.5',
          routerLink: './asset'
        }"
      >
        <vs-property-set-values
          slot="subtitle"
          [propertySetId]="asset.propertySetId"
          [propertyValues]="(claim.propertySetSubmission ?? claim.warranty?.propertySetSubmission)?.value || {}"
          [dimensionValues]="(claim.propertySetSubmission ?? claim.warranty?.propertySetSubmission)?.dimensionValues || {}"
          [showHidden]="true"
          [firstOnly]="true"
        ></vs-property-set-values>
      </vs-info-card>

      <vs-info-card
        *ngIf="claim.warranty as warranty"
        class="basis-[99.8%] sm:min-w-[256px] min-w-[99.8%]"
        [class]="claim.warranty.distributor ? 'sm:basis-[24.5%] sm:max-w-[24.5%]' : 'sm:basis-[32.8%] sm:min-w-[32.8%]' "
        [config]="{
          icon: 'folder-shield',

          title: warranty.plan?.title,
          subtitle: warranty.contractNumber,

          routerLink: (canViewWarranty$ | async) ? '../../warranty/' + claim.warranty.id : undefined
        }"
      >
        <vs-badge *vsLet="warrantyTheme(claim) as theme" slot="header-end" [theme]="theme">
          <vs-icon name="circle" type="solid" size="8" [class]="'text-' + theme + '-500'"></vs-icon>
        </vs-badge>
      </vs-info-card>

      <vs-info-card
        *ngIf="claim.warranty?.distributor as distributor"
        class="sm:basis-[24.5%] sm:max-w-[24.5%] basis-[99.8%] sm:min-w-[256px] min-w-[99.8%]"
        [config]="{
          icon: 'package',

          title: distributor.name,
          subtitle: distributor.email ?? distributor.phone ?? distributor.id,

          routerLink: (canViewDistributor$ | async) ? '../../distributors/' + distributor?.id : undefined
        }"
      ></vs-info-card>
    </div>

    <vs-divider></vs-divider>

    <div *ngIf="canViewLifecycle$ | async" class="my-4">
      <ws-claim-lifecycle [claim]="claim"></ws-claim-lifecycle>
    </div>

    <div class="flex max-w-full" [class.flex-col]="isMobile" [class.overflow-x-hidden]="!timeline.open">
      <div class="transition-all" [style.width]="!isMobile ? timeline.open ? '55%' : '98%' : '100%'">
        <div class="mb-4">
          <vs-tab-nav *vsLet="tabs$ | async as tabs" theme="primary" [config]="{ items: tabs ?? [] }"> </vs-tab-nav>
        </div>

        <router-outlet></router-outlet>
      </div>

      <div class="w-full transition-all" [style.max-width]="!isMobile ? timeline.open ? '45%' : '42px' : '100%'">
        <ws-claim-timeline #timeline [claim]="claim"></ws-claim-timeline>
      </div>
    </div>

    <vs-dialog #expireClaimDialog [config]="{ dialogContainerClass: 'max-w-[600px]'}">
      <vs-dialog-header
        [config]="{
          mode: 'simple',

          icon: 'file-x-02',
          iconTheme: 'warn',

          title: 'Mark Claim as Expired',
          subtitle: 'Are you sure you want to mark <strong>Claim #' + claim.claimNumber + '</strong> as expired? This action cannot be undone.'
        }"
      ></vs-dialog-header>

      <div class="flex gap-2">
        <button vs-button appearance="outline" class="flex-1" (click)="expireClaimDialog.close()">Back</button>
        <button vs-button theme="danger" class="flex-1" (click)="expire(claim)">Expire Claim</button>
      </div>
    </vs-dialog>

    <vs-dialog #cancelClaimDialog [config]="{ dialogContainerClass: 'max-w-[600px]'}">
      <vs-dialog-header
        [config]="{
          mode: 'simple',

          icon: 'file-x-02',
          iconTheme: 'warn',

          title: 'Cancel Claim',
          subtitle: 'Are you sure you want to cancel <strong>Claim #' + claim.claimNumber + '</strong>? This action cannot be undone.'
        }"
      ></vs-dialog-header>

      <vs-form-field
        class="mt-4 mb-8 mx-auto"
        [config]="{
          label: { text: 'Cancellation Reason', for: 'cancellation-reason' }
        }"
      >
        <input
          id="cancellation-reason"
          vsInput
          type="text"
          placeholder="Reason for cancellation..."
          [formControl]="cancelReason"
        />
      </vs-form-field>

      <div class="flex gap-2">
        <button vs-button appearance="outline" class="flex-1" (click)="cancelClaimDialog.close()">Back</button>
        <button
          vs-button
          theme="danger"
          [disabled]="!cancelReason.value"
          class="flex-1"
          (click)="cancel(claim, cancelReason.value ?? '')"
        >
          Cancel Claim
        </button>
      </div>
    </vs-dialog>

    <ws-claim-close-dialog #closeClaimDialog></ws-claim-close-dialog>

    <ws-claim-item-details-dialog #addClaimItem></ws-claim-item-details-dialog>
  </vs-app-page>
</ng-container>
